import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { IRequiredBackCountActivityModel } from 'src/app/settings/interfaces/required-back-count-activity-model.interface';
import { endpoints } from '../../constants/endpoints';
import { BACK_COUNT, COMPOUNDING, ENABLE_C3_TO_C5_DRUGS, INVENTORY_LOCATIONS } from '../../constants/setting-constants';
import { BAATypeEnum } from '../../interfaces/BAATypeEnum';
import { IC3C5SettingValue } from '../../interfaces/IC3C5SettingValue.interface';
import { IPharmacyDetails } from '../../interfaces/pharmacy-details.interface';
import { IPharmacy } from '../../interfaces/pharmacy.interface';
import { PricingOption } from '../../interfaces/PricingOption';
import { TokensEnum } from '../../interfaces/TokensEnum';
import { UserRolesEnum } from '../../interfaces/UserRolesEnum';
import { CookiesService } from '../../services/cookies.service';
import { ErrorService } from '../../services/error.service';
import { ClearStateAndCookies, SetPharmacyId } from '../auth/auth.actions';
import { AuthState } from '../auth/auth.state';
import * as Act from './pharmacy.actions';
import { GetUserPharmacies } from './pharmacy.actions';

export interface PharmacyStateModel {
  loading: boolean;
  success: boolean;
  pharmacies: IPharmacy[];
  pharmacyNPI: number | null;
  pharmacyBaa: BAATypeEnum;
  pharmacyForm: any | null;
  pharmacyData: IPharmacyDetails; // long form
  currentPharmacy: IPharmacy; // short form
  pricingOption: PricingOption;
}

@State<PharmacyStateModel>({
  name: 'pharmacy',
})

@Injectable()
export class PharmacyState {

  constructor(
    private http: HttpClient,
    private store: Store,
    private cookiesService: CookiesService,
    private errorService: ErrorService,
    private router: Router
  ) {
  }

  @Selector()
  static loading(state: PharmacyStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static success(state: PharmacyStateModel): boolean {
    return state.success;
  }

  @Selector()
  static currentPharmacy(state: PharmacyStateModel): any {
    return state.currentPharmacy;
  }

  @Selector()
  static pharmacyNPI(state: PharmacyStateModel): number {
    return state.pharmacyNPI;
  }

  @Selector()
  static pharmacyData(state: PharmacyStateModel): any {
    return state.pharmacyData;
  }

  @Selector()
  static pharmacyForm(state: PharmacyStateModel): any {
    return state.pharmacyForm;
  }

  @Selector()
  static pharmacies(state: PharmacyStateModel): IPharmacy[] {
    return state.pharmacies;
  }

  @Selector()
  static pharmaciesICanEdit(state: PharmacyStateModel): IPharmacy[] {
    return state.pharmacies.filter(f => f.role < UserRolesEnum.PharmacyAdmin);
  }

  @Selector()
  static pricingOption(state: PharmacyStateModel): PricingOption {
    return state.pricingOption;
  }

  @Selector()
  static enableC3toC5Drugs(state: PharmacyStateModel): boolean {
    const enableC3toC5DrugsSetting = state?.currentPharmacy?.preferences?.find(x => x.name === ENABLE_C3_TO_C5_DRUGS);
    if (enableC3toC5DrugsSetting) {
      return !enableC3toC5DrugsSetting.deActivationDate;
    }
    return false;
  }

  @Selector()
  static enableC3toC5DrugsValue(state: PharmacyStateModel): IC3C5SettingValue {
    const enableC3toC5DrugsSetting = state?.currentPharmacy?.preferences?.find(x => x.name === ENABLE_C3_TO_C5_DRUGS);
    if (enableC3toC5DrugsSetting) {
      return enableC3toC5DrugsSetting?.value && JSON.parse(enableC3toC5DrugsSetting.value?.toString());
    }
    return null;
  }

  @Selector()
  static inventoryLocationsEnabled(state: PharmacyStateModel): boolean {
    const inventoryLocationsSetting = state?.currentPharmacy?.preferences?.find(x => x.name === INVENTORY_LOCATIONS);
    if (inventoryLocationsSetting) {
      return !inventoryLocationsSetting.deActivationDate;
    }
    return false;
  }

  @Selector()
  static backCountEnabled(state: PharmacyStateModel): boolean {
    return this.settingEnabled(state, BACK_COUNT);
  }

  @Selector()
  static backCountValue(state: PharmacyStateModel): IRequiredBackCountActivityModel[] {
    const backCountSetting = state?.currentPharmacy?.preferences?.find(x => x.name === BACK_COUNT);
    if (backCountSetting) {
      const value = backCountSetting?.value && JSON.parse(backCountSetting.value);
      return value?.Activities;
    }
    return null;
  }

  @Selector()
  static compoundingEnabled(state: PharmacyStateModel): boolean {
    return this.settingEnabled(state, COMPOUNDING);
  }

  @Action(Act.GetUserPharmacies)
  getUserPharmacies(ctx: StateContext<PharmacyStateModel>, {}: Act.GetUserPharmacies): Observable<IPharmacy[]> {
    const userId = this.store.selectSnapshot(AuthState.userId);
    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);
    ctx.patchState({loading: true, pharmacies: []});
    return this.http.get(endpoints.pharmacy.getPharmaciesByUserId + userId)
      .pipe(
        tap((resp: any) => {
          const pharmacies = (resp.value?.pharmacies) || [];
          const currentPharmacy = pharmacies.find(item => item.id === pharmacyId);
          ctx.patchState({
            pharmacies,
            currentPharmacy,
            loading: false,
            pricingOption: currentPharmacy?.pricingOption
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetAllPharmacies)
  getAllPharmacies(ctx: StateContext<PharmacyStateModel>, {}: Act.GetAllPharmacies): Observable<IPharmacy[]> {
    ctx.patchState({loading: true});
    return this.http.get(endpoints.pharmacy.getAllPharmacies)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SetCurrentPharmacy)
  setCurrentPharmacy(ctx: StateContext<PharmacyStateModel>, {pharmacyId}: Act.SetCurrentPharmacy): void {
    const pharmacies = ctx.getState().pharmacies;

    ctx.patchState({
      currentPharmacy: pharmacies.find(item => item.id === pharmacyId),
    });
  }

  @Action(Act.GetPharmacyByNPI)
  getPharmacyByNPI(ctx: StateContext<PharmacyStateModel>, {npiNumber}: Act.GetPharmacyByNPI): Observable<any> {
    ctx.patchState({loading: true, pharmacyData: null});

    return this.http.get(endpoints.npi.getNPI + npiNumber)
      .pipe(
        tap((resp: any) => {
          let key = '';
          if (!resp.success) {
            key = this.errorService.getErrorEnumKeyValue(resp);
          }
          ctx.patchState(
            {
              loading: false,
              pharmacyData: (resp.success && resp.value && resp.value.results && resp.value.results[0])
                || {empty: true, messageEnumKey: key},
            }
          );
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetPharmacyById)
  getPharmacyById(ctx: StateContext<PharmacyStateModel>, {id}: Act.GetPharmacyById): Observable<any> {
    ctx.patchState({loading: true, pharmacyData: null});

    return this.http.get(endpoints.pharmacy.getPharmacy + `?id=${id}`)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState(
            {
              loading: false,
              pharmacyData: responseData?.value?.pharmacyDetails,
              pharmacyForm: responseData?.value?.pharmacyDetails
            }
          );
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SavePharmacyNPI)
  SavePharmacyNPI(ctx: StateContext<PharmacyStateModel>, {npi}: Act.SavePharmacyNPI): void {
    ctx.patchState({pharmacyNPI: npi});
  }

  @Action(Act.SavePharmacySelectedBaa)
  savePharmacySelectedBaa(ctx: StateContext<PharmacyStateModel>, {baa}: Act.SavePharmacySelectedBaa): void {
    ctx.patchState({pharmacyBaa: baa});
  }


  @Action(Act.SavePharmacyForm)
  savePharmacyForm(ctx: StateContext<PharmacyStateModel>, {pharmacyForm}: Act.SavePharmacyForm): void {
    ctx.patchState({pharmacyForm});
  }

  @Action(Act.SavePharmacyDetails)
  savePharmacyDetails(ctx: StateContext<PharmacyStateModel>, {}: Act.SavePharmacyDetails): Observable<any> {
    const companyId = this.cookiesService.getCookies(TokensEnum.CompanyId);

    ctx.patchState({loading: true, success: null});
    const pharmacyForm = {...ctx.getState().pharmacyForm};
    pharmacyForm.companyId = companyId;
    pharmacyForm.createByEmail = this.store.selectSnapshot(AuthState.currentUser)?.email;
    pharmacyForm.pricingOption = ctx.getState()?.pricingOption ?? 2;
    pharmacyForm.baa = ctx.getState().pharmacyBaa;
    
    //don't wait until pharmacy gets created
    if (!ctx.getState()?.pharmacies?.length) {
      setTimeout(() => {
        this.store.dispatch(new ClearStateAndCookies());
        this.router.navigateByUrl('account-under-review');
      });
    } 

    return this.http.put(endpoints.pharmacy.createPharmacy, pharmacyForm)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData.success,
          });
          if (responseData.value?.pharmacyId) {
              if (ctx.getState()?.pharmacies?.length) {
              this.store.dispatch(new SetPharmacyId(responseData.value.pharmacyId));
              this.store.dispatch(new GetUserPharmacies());
            }
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.UpdatePharmacyDetails)
  updatePharmacyDetails(ctx: StateContext<PharmacyStateModel>, {pharmacyDetails}: Act.UpdatePharmacyDetails): Observable<any> {
    ctx.patchState({loading: true, pharmacyData: null, success: null});

    pharmacyDetails.companyId = this.cookiesService.getCookies(TokensEnum.CompanyId);
    pharmacyDetails.createByEmail = this.store.selectSnapshot(AuthState.currentUser)?.email;

    return this.http.patch(endpoints.pharmacy.updatePharmacy, pharmacyDetails)
      .pipe(
        tap((responseData: any) => {
          const address = `${pharmacyDetails?.streetAddress} ${pharmacyDetails?.city}, ${pharmacyDetails?.state}`;
          const currentPharmacy = ctx.getState().currentPharmacy;
          ctx.patchState({
            loading: false,
            success: responseData.success,
            pharmacyData: pharmacyDetails,
            pharmacies: ctx.getState().pharmacies.map(x => x.id === pharmacyDetails?.id
              ? {...x, name: pharmacyDetails?.pharmacyName, address }
              : x ),
            currentPharmacy: currentPharmacy.id === pharmacyDetails?.id
              ? {...currentPharmacy, name: pharmacyDetails?.pharmacyName, address }
              : currentPharmacy
          });

          if (responseData.value?.pharmacyId) {
            this.store.dispatch(new SetPharmacyId(responseData.value.pharmacyId));
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SetPricingOption)
  setPricingOption(ctx: StateContext<PharmacyStateModel>, {pricingOption}: Act.SetPricingOption): void {
    ctx.patchState({pricingOption});
  }

  @Action(Act.EnableC3toC5Drugs)
  enableC3toC5Drugs(ctx: StateContext<PharmacyStateModel>, {pin, value}: Act.EnableC3toC5Drugs): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.post(endpoints.preference.toggleC3C5, {pin, value})
      .pipe(
        tap((responseData: any) => {
          if (responseData.success) {
            this.handlePreferencesResponse(responseData.value, ENABLE_C3_TO_C5_DRUGS, ctx);
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.UpdateDrugsToTrack)
  updateDrugsToTrack(ctx: StateContext<PharmacyStateModel>, {pin, drugsOfInterest}: Act.UpdateDrugsToTrack): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.patch(endpoints.preference.updateDrugsToTrack, {pin, drugsOfInterest})
      .pipe(
        tap((responseData: any) => {
          if (responseData.success) {
            this.handlePreferencesResponse(responseData.value, ENABLE_C3_TO_C5_DRUGS, ctx);
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ToggleInventoryLocations)
  toggleInventoryLocations(ctx: StateContext<PharmacyStateModel>, {pin}: Act.ToggleInventoryLocations): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.post(endpoints.preference.toggleInventoryLocations, {pin})
      .pipe(
        tap((responseData: any) => {
          if(responseData.success) {
            this.handlePreferencesResponse(responseData.value?.setting, INVENTORY_LOCATIONS, ctx);
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ToggleBackCount)
  toggleBackCount(ctx: StateContext<PharmacyStateModel>, {request}: Act.ToggleBackCount): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.post(endpoints.preference.toggleBackCount, request)
      .pipe(
        tap((responseData: any) => {
          if(responseData.success) {
            this.handlePreferencesResponse(responseData.value, BACK_COUNT, ctx);
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.UpdateBackCount)
  updateBackCount(ctx: StateContext<PharmacyStateModel>, {request}: Act.UpdateBackCount): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.patch(endpoints.preference.updateBackCount, request)
      .pipe(
        tap((responseData: any) => {
          if(responseData.success) {
            this.handlePreferencesResponse(responseData.value, BACK_COUNT, ctx);
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ToggleCompounding)
  toggleCompounding(ctx: StateContext<PharmacyStateModel>, {pin}: Act.ToggleCompounding): Observable<any> {
    ctx.patchState({loading: true});
    return this.http.post(endpoints.preference.toggleCompounding, {pin})
      .pipe(
        tap((responseData: any) => {
          if(responseData.success) {
            this.handlePreferencesResponse(responseData.value, COMPOUNDING, ctx);
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  private handlePreferencesResponse(setting: any, settingName: string, ctx: StateContext<PharmacyStateModel>): void {
      let preferences = [...ctx.getState().currentPharmacy?.preferences];
      const current = preferences.find(x => x.name === settingName);
      if (!current) {
        preferences.push({...setting, name: settingName});
      } else {
        preferences = preferences.map(x => x.settingId === setting?.settingId ? 
             ({...x, deActivationDate: setting.deActivationDate, value: setting.value}) : x);
    }
    ctx.patchState({currentPharmacy: {...ctx.getState().currentPharmacy, preferences}});
  }

  private static settingEnabled(state: PharmacyStateModel, settingName: string): boolean {
    const backCountSetting = state?.currentPharmacy?.preferences?.find(x => x.name === settingName);
    if (backCountSetting) {
      return !backCountSetting.deActivationDate;
    }
    return false;
  }
}
